import { blogPosts, authors } from '../data/blogPosts';
import { documentationPages, getAllDocumentationSections } from '../data/documentation';

/**
 * Search all content (blog posts and documentation) with comprehensive filtering and sorting
 */
export const searchAllContent = ({
  query = '',
  tags = [],
  author = '',
  dateRange = { start: null, end: null },
  sortBy = 'date-desc',
  limit = null,
  contentTypes = ['blog', 'docs'] // Filter by content type
}) => {
  let results = [];

  // Add blog posts
  if (contentTypes.includes('blog')) {
    const blogResults = blogPosts.map(post => ({
      ...post,
      contentType: 'blog',
      url: `/blog/${post.slug}`,
      image: post.ogImage || '/img/pwgen-social-card.png' // strategic fallback
    }));
    results = results.concat(blogResults);
  }

  // Add documentation pages
  if (contentTypes.includes('docs')) {
    const docSections = getAllDocumentationSections();
    const docResults = [];
    
    docSections.forEach(section => {
      section.pages.forEach(pageKey => {
        const docPage = documentationPages[pageKey];
        if (docPage) {
          docResults.push({
            slug: `${section.key}-${pageKey}`,
            title: docPage.title,
            excerpt: docPage.description || '',
            content: docPage.content || '',
            tags: [section.key, 'documentation', 'guide'],
            authors: ['kief'],
            date: new Date('2024-01-01'), // Default date for docs
            contentType: 'docs',
            url: `/docs/${section.key}/${pageKey}`,
            section: section.title,
            sectionKey: section.key,
            pageKey: pageKey,
            image: '/img/KS-Logo-Full-Light.png' // Default docs image
          });
        }
      });
    });
    
    results = results.concat(docResults);
  }

  // Text search across title, excerpt, content, and tags
  if (query.trim()) {
    const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
    
    results = results.filter(item => {
      const searchableText = [
        item.title,
        item.excerpt,
        item.content,
        item.tags.join(' '),
        item.metaDescription || '',
        (item.keywords || []).join(' '),
        item.section || ''
      ].join(' ').toLowerCase();
      
      // All search terms must be found (AND logic)
      return searchTerms.every(term => searchableText.includes(term));
    });

    // Add relevance scoring for sorting
    results = results.map(item => {
      let score = 0;
      const titleLower = item.title.toLowerCase();
      const excerptLower = item.excerpt.toLowerCase();
      const contentLower = item.content.toLowerCase();
      
      searchTerms.forEach(term => {
        // Higher score for matches in title
        if (titleLower.includes(term)) score += 10;
        // Medium score for matches in excerpt
        if (excerptLower.includes(term)) score += 5;
        // Lower score for matches in content
        if (contentLower.includes(term)) score += 1;
        // Bonus for exact phrase matches
        if (titleLower.includes(query.toLowerCase())) score += 20;
      });
      
      return { ...item, relevanceScore: score };
    });
  }

  // Filter by tags (only applies to blog posts)
  if (tags.length > 0) {
    results = results.filter(item => 
      item.contentType === 'docs' || tags.every(tag => item.tags.includes(tag))
    );
  }

  // Filter by author (only applies to blog posts)
  if (author) {
    results = results.filter(item => 
      item.contentType === 'docs' || item.authors.includes(author)
    );
  }

  // Filter by date range (only applies to blog posts)
  if (dateRange.start) {
    results = results.filter(item => 
      item.contentType === 'docs' || item.date >= dateRange.start
    );
  }
  if (dateRange.end) {
    results = results.filter(item => 
      item.contentType === 'docs' || item.date <= dateRange.end
    );
  }

  // Sort results
  results.sort((a, b) => {
    switch (sortBy) {
      case 'date-asc':
        return a.date - b.date;
      case 'date-desc':
        return b.date - a.date;
      case 'title-asc':
        return a.title.localeCompare(b.title);
      case 'title-desc':
        return b.title.localeCompare(a.title);
      case 'relevance':
        if (query.trim()) {
          return (b.relevanceScore || 0) - (a.relevanceScore || 0);
        }
        return b.date - a.date; // fallback to date
      default:
        return b.date - a.date;
    }
  });

  // Apply limit if specified
  if (limit && limit > 0) {
    results = results.slice(0, limit);
  }

  return results;
};

/**
 * Search blog posts with comprehensive filtering and sorting
 * @deprecated Use searchAllContent instead
 */
export const searchBlogPosts = ({
  query = '',
  tags = [],
  author = '',
  dateRange = { start: null, end: null },
  sortBy = 'date-desc',
  limit = null
}) => {
  let results = [...blogPosts];

  // Text search across title, excerpt, content, and tags
  if (query.trim()) {
    const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
    
    results = results.filter(post => {
      const searchableText = [
        post.title,
        post.excerpt,
        post.content,
        post.tags.join(' '),
        post.metaDescription || '',
        (post.keywords || []).join(' ')
      ].join(' ').toLowerCase();
      
      // All search terms must be found (AND logic)
      return searchTerms.every(term => searchableText.includes(term));
    });

    // Add relevance scoring for sorting
    results = results.map(post => {
      let score = 0;
      const titleLower = post.title.toLowerCase();
      const excerptLower = post.excerpt.toLowerCase();
      const contentLower = post.content.toLowerCase();
      
      searchTerms.forEach(term => {
        // Higher score for matches in title
        if (titleLower.includes(term)) score += 10;
        // Medium score for matches in excerpt
        if (excerptLower.includes(term)) score += 5;
        // Lower score for matches in content
        if (contentLower.includes(term)) score += 1;
        // Bonus for exact phrase matches
        if (titleLower.includes(query.toLowerCase())) score += 20;
      });
      
      return { ...post, relevanceScore: score };
    });
  }

  // Filter by tags
  if (tags.length > 0) {
    results = results.filter(post => 
      tags.every(tag => post.tags.includes(tag))
    );
  }

  // Filter by author
  if (author) {
    results = results.filter(post => post.authors.includes(author));
  }

  // Filter by date range
  if (dateRange.start) {
    results = results.filter(post => post.date >= dateRange.start);
  }
  if (dateRange.end) {
    results = results.filter(post => post.date <= dateRange.end);
  }

  // Sort results
  results.sort((a, b) => {
    switch (sortBy) {
      case 'date-asc':
        return a.date - b.date;
      case 'date-desc':
        return b.date - a.date;
      case 'title-asc':
        return a.title.localeCompare(b.title);
      case 'title-desc':
        return b.title.localeCompare(a.title);
      case 'relevance':
        if (query.trim()) {
          return (b.relevanceScore || 0) - (a.relevanceScore || 0);
        }
        return b.date - a.date; // fallback to date
      default:
        return b.date - a.date;
    }
  });

  // Apply limit if specified
  if (limit && limit > 0) {
    results = results.slice(0, limit);
  }

  return results;
};

/**
 * Get all unique tags from blog posts with post counts
 */
export const getAllTagsWithCounts = () => {
  const tagCounts = {};
  
  blogPosts.forEach(post => {
    post.tags.forEach(tag => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });
  });

  return Object.entries(tagCounts)
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Get related posts based on shared tags
 */
export const getRelatedPosts = (currentPost, limit = 3) => {
  if (!currentPost) return [];

  const otherPosts = blogPosts.filter(post => post.slug !== currentPost.slug);
  
  // Score posts based on shared tags
  const scoredPosts = otherPosts.map(post => {
    const sharedTags = post.tags.filter(tag => currentPost.tags.includes(tag));
    return {
      ...post,
      similarity: sharedTags.length
    };
  });

  // Sort by similarity, then by date
  return scoredPosts
    .sort((a, b) => {
      if (a.similarity !== b.similarity) {
        return b.similarity - a.similarity;
      }
      return b.date - a.date;
    })
    .slice(0, limit);
};

/**
 * Get posts by specific tag
 */
export const getPostsByTag = (tag) => {
  return blogPosts.filter(post => post.tags.includes(tag))
    .sort((a, b) => b.date - a.date);
};

/**
 * Get search suggestions based on partial query
 */
export const getSearchSuggestions = (query, limit = 5) => {
  if (!query.trim()) return [];

  const queryLower = query.toLowerCase();
  const suggestions = new Set();
  
  // Add matching titles
  blogPosts.forEach(post => {
    if (post.title.toLowerCase().includes(queryLower)) {
      suggestions.add(post.title);
    }
  });

  // Add matching tags
  const allTags = getAllTagsWithCounts();
  allTags.forEach(({ tag }) => {
    if (tag.toLowerCase().includes(queryLower)) {
      suggestions.add(tag);
    }
  });

  // Add matching keywords
  blogPosts.forEach(post => {
    if (post.keywords) {
      post.keywords.forEach(keyword => {
        if (keyword.toLowerCase().includes(queryLower)) {
          suggestions.add(keyword);
        }
      });
    }
  });

  return Array.from(suggestions).slice(0, limit);
};

/**
 * Get popular/trending posts based on tags and recency
 */
export const getTrendingPosts = (limit = 6) => {
  const popularTags = ['security', 'rust', 'password-manager', 'release'];
  
  return blogPosts
    .map(post => {
      let trendScore = 0;
      
      // Recent posts get higher score
      const daysSincePost = (Date.now() - post.date.getTime()) / (1000 * 60 * 60 * 24);
      trendScore += Math.max(0, 100 - daysSincePost);
      
      // Posts with popular tags get higher score
      post.tags.forEach(tag => {
        if (popularTags.includes(tag)) {
          trendScore += 50;
        }
      });
      
      return { ...post, trendScore };
    })
    .sort((a, b) => b.trendScore - a.trendScore)
    .slice(0, limit);
};

/**
 * Get featured posts (manually curated or based on criteria)
 */
export const getFeaturedPosts = (limit = 3) => {
  // For now, return most recent release posts
  const releaseKeywords = ['release', 'announcement', 'investment'];
  
  return blogPosts
    .filter(post => post.tags.some(tag => releaseKeywords.includes(tag)))
    .sort((a, b) => b.date - a.date)
    .slice(0, limit);
};

/**
 * Get author information with post counts
 */
export const getAuthorsWithCounts = () => {
  const authorCounts = {};
  
  blogPosts.forEach(post => {
    post.authors.forEach(authorId => {
      authorCounts[authorId] = (authorCounts[authorId] || 0) + 1;
    });
  });

  return Object.entries(authorCounts).map(([authorId, count]) => ({
    ...authors[authorId],
    postCount: count
  }));
};

/**
 * Format search result highlights
 */
export const highlightSearchTerms = (text, query) => {
  if (!query.trim()) return text;
  
  const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
  let highlightedText = text;
  
  searchTerms.forEach(term => {
    const regex = new RegExp(`(${term})`, 'gi');
    highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
  });
  
  return highlightedText;
};